import {
  ClipboardCheck,
  Activity,
  Diff,
  FolderKanban,
  GitPullRequestArrow,
  LayoutDashboard,
  Moon,
  type LucideIcon,
} from "lucide-react";
import type { WorkspaceArtifact, WorkspaceArtifactKind } from "../types";
import { ProofIcon } from "@/components/codesmith/proof/proofIcon";
import {
  dreamFindingCount,
  dreamFindingsLabel,
} from "@/components/codesmith/dream/dreamReport";
import { REVIEW_OUTCOME_LABELS } from "@/components/codesmith/review/reviewOutcome";

type ArtifactOf<K extends WorkspaceArtifactKind> = Extract<
  WorkspaceArtifact,
  { kind: K }
>;

interface WorkspaceArtifactKindSpec<K extends WorkspaceArtifactKind> {
  icon: LucideIcon;
  /**
   * Opens as a tab on every first visit, beside the first-visit tab rather
   * than instead of it, and arrives already seen; leads only when nothing
   * else opens (see workspaceTabsState).
   */
  pinned?: true;
  /** Opens at the front of the strip the first time the catalog lists it; closes like any tab (see workspaceTabsState). */
  leading?: true;
  /** Title first; the rest is matched but not displayed. */
  searchableFields: (artifact: ArtifactOf<K>) => string[];
  /** Shown under the title in a search result's tooltip, e.g. `PR #41`. */
  label?: (artifact: ArtifactOf<K>) => string;
  /** Tab text when the title is too long to identify the tab at a glance; the title stays in the tab's tooltip. */
  tabTitle?: (artifact: ArtifactOf<K>) => string;
  /** Screen-reader-only type name for kinds without a `label`. */
  srLabel?: string;
  /** Distinguishes identically titled artifacts, e.g. `repo#41`, `v2`. */
  detail?: (artifact: ArtifactOf<K>) => string | undefined;
}

/**
 * Everything the panel knows about a kind besides how to render it (see
 * WorkspacePanel). A new kind is a new entry here plus a renderer; the
 * exhaustive record makes both compile errors until done.
 */
export const WORKSPACE_ARTIFACT_KINDS: {
  [K in WorkspaceArtifactKind]: WorkspaceArtifactKindSpec<K>;
} = {
  changes: {
    icon: Diff,
    leading: true,
    searchableFields: (artifact) => [artifact.title, ...artifact.repositories],
  },
  project: {
    icon: FolderKanban,
    pinned: true,
    searchableFields: (artifact) => [artifact.title],
  },
  pr: {
    icon: GitPullRequestArrow,
    searchableFields: (artifact) => [
      artifact.title,
      artifact.repository,
      `#${artifact.prNumber}`,
      `${artifact.repository}#${artifact.prNumber}`,
    ],
    label: (artifact) => `PR #${artifact.prNumber}`,
    tabTitle: (artifact) =>
      `#${artifact.prNumber} ${artifact.repository.split("/").pop()}`,
    detail: (artifact) =>
      `${artifact.repository.split("/").pop()}#${artifact.prNumber}`,
  },
  canvas: {
    icon: LayoutDashboard,
    searchableFields: (artifact) => [artifact.title, artifact.filename],
    srLabel: "Canvas",
    detail: (artifact) => `v${artifact.version}`,
  },
  dream_report: {
    icon: Moon,
    searchableFields: (artifact) => [artifact.title, "dream"],
    label: () => "Dream",
    detail: (artifact) =>
      artifact.result
        ? dreamFindingsLabel(dreamFindingCount(artifact.result))
        : undefined,
  },
  review_report: {
    icon: ClipboardCheck,
    searchableFields: (artifact) => [
      artifact.title,
      ...(artifact.repository ? [artifact.repository] : []),
      `#${artifact.prNumber}`,
      ...(artifact.repository
        ? [`${artifact.repository}#${artifact.prNumber}`]
        : []),
      ...(artifact.report
        ? [REVIEW_OUTCOME_LABELS[artifact.report.review_result.outcome]]
        : []),
    ],
    label: () => "Review",
    detail: (artifact) =>
      artifact.repository
        ? `${artifact.repository.split("/").pop()}#${artifact.prNumber}`
        : `#${artifact.prNumber}`,
  },
  proof_report: {
    icon: ProofIcon,
    searchableFields: (artifact) => [
      artifact.title,
      ...(artifact.repository ? [artifact.repository] : []),
      `#${artifact.prNumber}`,
      ...(artifact.repository
        ? [`${artifact.repository}#${artifact.prNumber}`]
        : []),
      artifact.run.status,
    ],
    label: () => "Proof",
    detail: (artifact) =>
      artifact.repository
        ? `${artifact.repository.split("/").pop()}#${artifact.prNumber}`
        : `#${artifact.prNumber}`,
  },
  vm_metrics: {
    icon: Activity,
    searchableFields: (artifact) => [artifact.title, artifact.vmId, "metrics"],
    label: () => "VM metrics",
  },
};

function specFor<K extends WorkspaceArtifactKind>(
  kind: K,
): WorkspaceArtifactKindSpec<K> {
  return WORKSPACE_ARTIFACT_KINDS[kind];
}

export function workspaceArtifactIcon(kind: WorkspaceArtifactKind): LucideIcon {
  return specFor(kind).icon;
}

export function isPinnedArtifact(
  artifact: WorkspaceArtifact | undefined,
): boolean {
  return artifact !== undefined && specFor(artifact.kind).pinned === true;
}

export function isLeadingArtifact(artifact: WorkspaceArtifact): boolean {
  return specFor(artifact.kind).leading === true;
}

export function workspaceArtifactSearchableFields(
  artifact: WorkspaceArtifact,
): string[] {
  return specFor(artifact.kind).searchableFields(artifact);
}

export function workspaceArtifactLabel(
  artifact: WorkspaceArtifact,
): string | null {
  return specFor(artifact.kind).label?.(artifact) ?? null;
}

export function workspaceArtifactTabTitle(artifact: WorkspaceArtifact): string {
  return specFor(artifact.kind).tabTitle?.(artifact) ?? artifact.title;
}

export function workspaceArtifactSrLabel(
  artifact: WorkspaceArtifact,
): string | null {
  return specFor(artifact.kind).srLabel ?? null;
}

export function workspaceArtifactDetail(
  artifact: WorkspaceArtifact,
): string | null {
  return specFor(artifact.kind).detail?.(artifact) ?? null;
}
